import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import OwnerLayout from './components/owner/OwnerLayout.jsx';
import AuthRouteLayout from './components/owner/AuthRouteLayout.jsx';
import LoginPage from './pages/owner/LoginPage.jsx';
import ForgotPasswordPage from './pages/owner/ForgotPasswordPage.jsx';
import DashboardPage from './pages/owner/DashboardPage.jsx';
import CalendarPage from './pages/owner/CalendarPage.jsx';
import ReservationsPage from './pages/owner/ReservationsPage.jsx';
import AnalyticsPage from './pages/owner/AnalyticsPage.jsx';
import FinancialsPage from './pages/owner/FinancialsPage.jsx';
import OwnerLinksReportsPage from './pages/owner/OwnerLinksReportsPage.jsx';
import GuidesPage from './pages/owner/GuidesPage.jsx';
import LayoutQAPage from './pages/owner/LayoutQAPage.jsx';
import ResetPasswordPage from './pages/owner/ResetPasswordPage.jsx';
import ErrorBoundary from './components/owner/ErrorBoundary.jsx';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { ViewportProvider } from './contexts/ViewportContext.jsx';
import { ToastProvider } from './contexts/ToastContext.jsx';
import { DashboardPreferencesProvider } from './contexts/DashboardPreferencesContext.jsx';
import { ComfortModesProvider } from './contexts/ComfortModesContext.jsx';
import { AdaptiveIntelligenceProvider } from './contexts/AdaptiveIntelligenceContext.jsx';
import { AttentionItemsProvider } from './contexts/AttentionItemsContext.jsx';
import { SessionProvider } from './contexts/SessionContext.jsx';
import { ReportingPeriodProvider } from './contexts/ReportingPeriodContext.jsx';
import { getMe, logout } from './services/ownerApi.js';
import './index.css';
import './styles/comfort-modes.css';
import './styles/brand-personality.css';

// Build stamp for deployment verification
import { BUILD_SHA, BUILD_TIME } from './config/buildInfo.js';

if (typeof window !== 'undefined') {
  window.__OWNER_BUILD__ = {
    gitSha: BUILD_SHA || import.meta.env.VITE_GIT_SHA || 'dev',
    builtAt: BUILD_TIME || import.meta.env.VITE_BUILD_TIME || new Date().toISOString(),
    buildId: `${BUILD_SHA || 'dev'}-${Date.now()}`,
  };
  
  // Log build stamp on boot (always, not gated)
  console.log('[Owner Portal] Build Info:', window.__OWNER_BUILD__);
}

function OwnerApp() {
  // Auth state machine: 'checking' | 'authed' | 'unauthed'
  const [authState, setAuthState] = useState('checking');
  const [owner, setOwner] = useState(null);
  const [authMe, setAuthMe] = useState(null);

  // Track if /me has been called to prevent loops
  const meCalledRef = useRef(false);
  
  useEffect(() => {
    // Only call checkAuth once on mount
    // CRITICAL: After hard navigation (window.location.assign), this component remounts
    // and checkAuth() runs fresh with the new cookie
    if (!meCalledRef.current) {
      meCalledRef.current = true;
      checkAuth();
    }
  }, []);
  
  // CRITICAL: After hard navigation, the page reloads and this component remounts
  // The cookie should be available now, so checkAuth() should succeed
  // But we need to ensure checkAuth() completes before route protection checks authState

  const checkAuth = async () => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    const ts = Date.now();
    console.log('[AUTH_BOOT] start', { pathname, ts });

    try {
      const response = await getMe();
      console.log('[AUTH_BOOT] /me status', { status: 200, ts: Date.now() });

      // /me response: sendOwnerSuccess spreads data, so user/owner are at top level (not response.data)
      // Admin: response.user exists, response.owner may be absent. Owner: response.owner exists.
      const hasUserOrOwner = response?.user || response?.owner;
      const isSuccess = response.outcome === 'success' && hasUserOrOwner;

      if (isSuccess) {
        console.log('[AUTH_BOOT] authState -> authed', { ts: Date.now() });
        setAuthState('authed');
        const ownerData = response?.owner || response?.user || null;
        setOwner(ownerData);
        setAuthMe(response);
      } else {
        console.log('[AUTH_BOOT] authState -> unauthed', { reason: '!isSuccess', ts: Date.now() });
        setAuthState('unauthed');
        setAuthMe(null);
      }
    } catch (err) {
      console.log('[AUTH_BOOT] /me status', { status: err?.status ?? 'err', errorCode: err?.errorCode, ts: Date.now() });
      console.log('[AUTH_BOOT] authState -> unauthed', { status: err?.status, ts: Date.now() });
      setAuthState('unauthed');
      setAuthMe(null);
    }
  };

  /** Called by LoginPage after /me returns 200. Sets auth state; LoginPage then navigates. */
  const handleLoginSuccess = (meData) => {
    setAuthState('authed');
    // /me response: user/owner at top level (sendOwnerSuccess spreads data)
    setOwner(meData?.owner || meData?.user || null);
    setAuthMe(meData);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      // Continue with logout even if API call fails
    } finally {
      setAuthState('unauthed');
      setOwner(null);
      setAuthMe(null);
    }
  };

  // CRITICAL: Auth state machine - always render BrowserRouter, use route-level protection
  // State machine: 'checking' -> show loading, 'unauthed' -> allow /login, 'authed' -> allow all routes
  
  // Debug logging (once on mount)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authDebug = urlParams.get('auth_debug') === '1';
    if (authDebug) {
      console.log('[owner-main] Auth state:', { authState, pathname: window.location.pathname });
    }
  }, [authState]);

  // Show loading while checking auth
  if (authState === 'checking') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // CRITICAL: Always render BrowserRouter - use route-level protection instead of blocking router
  // This allows /login to render even when unauthed, preventing redirect loops
  // FIX: Wrap ALL routes in SessionProvider to prevent "useSession must be used within a SessionProvider" crash
  // SessionProvider handles unauthenticated state gracefully (401/403 treated as "not authenticated", no crash)
  // ViewportProvider: single source of truth for isMobile (breakpoint 768) so shell and pages stay in sync
  return (
    <ViewportProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SessionProvider initialAuthMe={authMe}>
        <Routes>
          <Route
            path="/"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <Navigate to="/calendar" replace />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              <AuthRouteLayout owner={null} onLogout={null}>
                <LoginPage onLoginSuccess={handleLoginSuccess} />
              </AuthRouteLayout>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <AuthRouteLayout owner={null} onLogout={null}>
                <ForgotPasswordPage />
              </AuthRouteLayout>
            }
          />
          <Route
            path="/dashboard"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <Navigate to="/calendar" replace />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/calendar"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <CalendarPage onLogout={handleLogout} />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reservations"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <ReservationsPage onLogout={handleLogout} />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/analytics"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <AnalyticsPage onLogout={handleLogout} />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/financials"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <FinancialsPage onLogout={handleLogout} />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Activity page hidden (reversible): direct URL redirects to calendar */}
          <Route path="/property-activity" element={<Navigate to="/calendar" replace />} />
          <Route
            path="/links-reports"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <OwnerLinksReportsPage onLogout={handleLogout} />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/guides"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  <GuidesPage onLogout={handleLogout} />
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/layout-qa"
            element={
              authState === 'authed' ? (
                <OwnerLayout owner={owner} onLogout={handleLogout} propertyName={null}>
                  {(() => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const debug = urlParams.get('debug') === '1' || urlParams.get('mock') === '1';
                    return debug ? <LayoutQAPage /> : <Navigate to="/calendar" replace />;
                  })()}
                </OwnerLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/owner/reset-password"
            element={
              <AuthRouteLayout owner={null} onLogout={null}>
                <ResetPasswordPage />
              </AuthRouteLayout>
            }
          />
        </Routes>
        </SessionProvider>
      </BrowserRouter>
    </ViewportProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <ComfortModesProvider>
          <AdaptiveIntelligenceProvider>
            <ToastProvider>
              <DashboardPreferencesProvider>
                {/* CRITICAL: SessionProvider is now at router level (BrowserRouter > SessionProvider > Routes) */}
                {/* This ensures ALL routes (including /login) are wrapped, preventing "useSession must be used" crash */}
                {/* SessionProvider handles unauthenticated state gracefully (401/403 = empty properties, no crash) */}
                <ReportingPeriodProvider>
                  <AttentionItemsProvider attentionItems={[]}>
                    <OwnerApp />
                  </AttentionItemsProvider>
                </ReportingPeriodProvider>
              </DashboardPreferencesProvider>
            </ToastProvider>
          </AdaptiveIntelligenceProvider>
        </ComfortModesProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
